import { IntlMessageFormat } from 'intl-messageformat'
import {
  CORE_SHELL_MESSAGE_REQUESTS, CORE_SHELL_MESSAGE_SOURCES,
} from './localization-sources.generated.js'
import {
  PORTAL_C01_PUBLIC_DYNAMIC_KEYS, PORTAL_C01_PUBLIC_MESSAGE_SOURCES,
  PORTAL_C01_PUBLIC_MESSAGE_REQUESTS, PORTAL_C01_PUBLIC_STATIC_REQUESTS,
} from './portal-c01-localization.generated.js'
import {
  PORTAL_C04_AUTHENTICATED_MESSAGE_REQUESTS, PORTAL_C04_AUTHENTICATED_MESSAGE_SOURCES,
} from './portal-c04-localization.generated.js'
import {
  PORTAL_C09_AUTHENTICATED_MESSAGE_REQUESTS, PORTAL_C09_AUTHENTICATED_MESSAGE_SOURCES,
} from './portal-c09-localization.generated.js'

export { CORE_SHELL_MESSAGE_REQUESTS, CORE_SHELL_MESSAGE_SOURCES }
export {
  PORTAL_C01_PUBLIC_DYNAMIC_KEYS, PORTAL_C01_PUBLIC_MESSAGE_SOURCES,
  PORTAL_C01_PUBLIC_MESSAGE_REQUESTS, PORTAL_C01_PUBLIC_STATIC_REQUESTS,
}
export {
  PORTAL_C04_AUTHENTICATED_MESSAGE_REQUESTS, PORTAL_C04_AUTHENTICATED_MESSAGE_SOURCES,
}
export {
  PORTAL_C09_AUTHENTICATED_MESSAGE_REQUESTS, PORTAL_C09_AUTHENTICATED_MESSAGE_SOURCES,
}

export const INTERFACE_LOCALES = ['en', 'nl', 'fr'] as const
export type InterfaceLocale = typeof INTERFACE_LOCALES[number]

export type LocalizedMessage = {
  key: string
  value: string
  requestedLocale: string | null
  resolvedLocale: string | null
  revision: number | null
  provenance: string | null
  fallback: boolean
  missing: boolean
}

export type LocalizationContext = {
  locale: InterfaceLocale
  localeSource: 'user' | 'persona' | 'deployment' | 'requested'
  messages: LocalizedMessage[]
}

export function sourceLocalizationContext(): LocalizationContext {
  return {
    locale: 'en',
    localeSource: 'deployment',
    messages: Object.entries(CORE_SHELL_MESSAGE_SOURCES).map(([key, value]) => ({
      key, value, requestedLocale: 'en', resolvedLocale: 'en', revision: null,
      provenance: 'source', fallback: false, missing: false,
    })),
  }
}

export function normalizeInterfaceLocale(value: unknown): InterfaceLocale | null {
  if (typeof value !== 'string') return null
  const base = value.trim().toLowerCase().split('-')[0]
  return INTERFACE_LOCALES.find((locale) => locale === base) ?? null
}

export function formatScalarMessage(pattern: string, values: Readonly<Record<string, unknown>> = {}): string {
  let rendered = pattern
  for (const [key, value] of Object.entries(values)) {
    rendered = rendered.replaceAll(`{{${key}}}`, value === null || value === undefined ? '' : String(value))
  }
  return rendered
}

export function isLocalizationContext(value: unknown): value is LocalizationContext {
  if (!value || typeof value !== 'object') return false
  const candidate = value as Partial<LocalizationContext>
  if (!normalizeInterfaceLocale(candidate.locale)) return false
  if (!['user', 'persona', 'deployment', 'requested'].includes(String(candidate.localeSource))) return false
  if (!Array.isArray(candidate.messages)) return false
  const keys = new Set<string>()
  for (const row of candidate.messages) {
    if (!row || typeof row !== 'object' || typeof row.key !== 'string' || typeof row.value !== 'string') return false
    if (keys.has(row.key)) return false
    keys.add(row.key)
    if (typeof row.fallback !== 'boolean' || typeof row.missing !== 'boolean') return false
    if (row.resolvedLocale !== null && normalizeInterfaceLocale(row.resolvedLocale) === null) return false
  }
  return true
}

/**
 * Broad catalogue migration uses ICU MessageFormat. It is portable across the
 * browser, Node and .NET ecosystems and expresses plural, select and rich-text
 * slots without executable translation strings. The first shell slice still
 * consumes the server-rendered scalar contract; this formatter pins and proves
 * the grammar before richer messages enter the governed catalogue.
 */
export function formatPortableMessage(
  pattern: string,
  locale: InterfaceLocale,
  values: Record<string, unknown> = {},
): string {
  const rendered = new IntlMessageFormat(pattern, locale).format(values as never)
  return Array.isArray(rendered) ? rendered.join('') : String(rendered)
}
